import { Button } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { formatCurrencyValue } from '@vend/utilities'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { getBasketSize } from '../../../../functions/lineItem'
import {
  useHotkeysService,
  useIsCalculatingSale,
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  selectLineItems,
  selectOutstandingBalance,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  selectIsTransitioningToPaymentScreen,
  setIsTransitioningToPaymentScreen,
} from '../../../../state/slices/uiStateSlice'
import { SaleServiceModal } from './sale-service-modal'
import { useCanTransitionToPaymentScreen } from './use-can-transition-to-payment-screen'
import { useSalePaymentLabel } from './use-sale-payment-label'
import { useShouldShowSaleServicesModal } from './use-show-sale-services-modal'
import { useTransitionToPaymentScreen } from './use-transition-to-payment-screen'

const HOTKEY = 'p'

export const SalePaymentButton: FunctionComponent = () => {
  const registerManager = useRegisterManager()
  const retailer = useRetailerSettingsManager().get()
  const hotkeysService = useHotkeysService()
  const saleCalculating = useIsCalculatingSale()
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const lineItems = useAppSelector(selectLineItems)
  const label = useSalePaymentLabel()
  const canTransitionToPaymentScreen = useCanTransitionToPaymentScreen()
  const transitionToPaymentScreen = useTransitionToPaymentScreen()
  const shouldShowSaleServicesModal = useShouldShowSaleServicesModal()
  const [serviceModalOpen, setServiceModalOpen] = useState(false)
  const isTransitioningToPaymentScreen = useAppSelector(
    selectIsTransitioningToPaymentScreen
  )
  const dispatch = useAppDispatch()

  const currentOutlet = registerManager.getActiveOutlet()
  const basketSize = getBasketSize(lineItems)

  const startPaymentTransition = useCallback(() => {
    if (canTransitionToPaymentScreen) {
      dispatch(setIsTransitioningToPaymentScreen(true))
    }
  }, [canTransitionToPaymentScreen, dispatch])

  useEffect(() => {
    const config = {
      combo: ['f4', `alt+${HOTKEY}`],
      summary: 'Go to the pay screen',
      tip: 'p',
      count: true,
      action: startPaymentTransition,
    }
    hotkeysService.add(config)

    return () => {
      hotkeysService.remove(config.combo)
    }
  }, [hotkeysService, startPaymentTransition, transitionToPaymentScreen])

  useEffect(() => {
    if (
      isTransitioningToPaymentScreen &&
      !saleCalculating &&
      canTransitionToPaymentScreen
    ) {
      dispatch(setIsTransitioningToPaymentScreen(false))

      if (shouldShowSaleServicesModal) {
        setServiceModalOpen(true)
      } else {
        transitionToPaymentScreen()
      }
    }
  }, [
    canTransitionToPaymentScreen,
    dispatch,
    isTransitioningToPaymentScreen,
    saleCalculating,
    shouldShowSaleServicesModal,
    transitionToPaymentScreen,
  ])

  if (!retailer) {
    return null
  }

  return (
    <>
      {serviceModalOpen && (
        <SaleServiceModal close={() => setServiceModalOpen(false)} />
      )}
      <Button
        id="wr-pay-button"
        type="button"
        data-cy="pay-btn"
        data-testid="pay-btn"
        className="btn-pay"
        jumbo
        variant={outstandingBalance < 0 ? 'no' : 'go'}
        disabled={
          !canTransitionToPaymentScreen || isTransitioningToPaymentScreen
        }
        onClick={startPaymentTransition}
        data-hotkey-tip={HOTKEY.toUpperCase()}
        data-track="sale-payment-button"
      >
        {/* Extra container is because display:flex doesn't work on a button on Firefox */}
        <div className="vd-flex vd-flex--justify-between">
          <div className="label-and-items-container">
            <div className="pay-action pay-action-label">{label}</div>
            <span className="vd-text--sub">
              <T
                _str="{basketSize, plural, one {{basketSize} item} other {{basketSize} items}}"
                basketSize={basketSize}
              />
            </span>
          </div>
          <div className="pay-action pay-action-value">
            {formatCurrencyValue(
              outstandingBalance,
              retailer.culture,
              currentOutlet.currency,
              currentOutlet.currency_symbol
            )}
          </div>
        </div>
      </Button>
    </>
  )
}
